import { t } from "~/i18n";
import type { AuthClientError } from "./auth-errors";
import type { InvitePreview } from "./invite-preview";
import { platformApiBaseUrl } from "./platform-api-origin";

/**
 * Joining a team by invite with an account the person already has: they sign
 * in on the invite page itself and accept with `POST /api/invites/accept`,
 * which moves them into the team.
 */

/** Who is signed in to platform-api in this browser, as `GET /api/me` says. */
export type InviteSession =
  | { readonly status: "signed-out" }
  | { readonly status: "signed-in"; readonly email: string }
  /** platform-api did not answer or failed: an outage, not an answer about the session. */
  | { readonly status: "unavailable" };

export async function fetchInviteSession(fetchImpl: typeof fetch = fetch): Promise<InviteSession> {
  try {
    const response = await fetchImpl(`${platformApiBaseUrl}/api/me`, {
      credentials: "include",
      cache: "no-store",
      headers: { accept: "application/json" },
    });
    if (response.status === 401) return { status: "signed-out" };
    if (!response.ok) return { status: "unavailable" };
    const body = (await response.json()) as { readonly user?: { readonly email?: unknown } };
    const email = body.user?.email;
    return typeof email === "string" && email.length > 0
      ? { status: "signed-in", email }
      : { status: "unavailable" };
  } catch {
    return { status: "unavailable" };
  }
}

/** A usable invite into a team, for an email that already has an account. */
export type ExistingAccountInvite = Extract<InvitePreview, { account: "existing" }> & {
  readonly team: string;
};

/** Whether the invite page offers signing in and joining instead of the registration form. */
export function isExistingAccountInvite(
  preview: InvitePreview | undefined,
): preview is ExistingAccountInvite {
  return preview?.status === "pending" && preview.account === "existing" && preview.team !== null;
}

/**
 * What the invite page shows an existing account. The invite belongs to its
 * email, whoever is signed in to this browser: an account with another email,
 * or none, signs in as the invited one right on the page.
 */
export type ExistingAccountStep =
  | { readonly kind: "checking" }
  | { readonly kind: "unavailable" }
  /** The invited account is in the team already; there is nothing to accept. */
  | { readonly kind: "already-member" }
  /** The invited account is not the one signed in: its password signs in and joins. */
  | {
      readonly kind: "sign-in-and-join";
      /** The other account signed in now, which signing in replaces. */
      readonly signedInAs: string | null;
      readonly movingFrom: string | null;
    }
  /** The invited account is signed in: one button joins. */
  | { readonly kind: "join"; readonly movingFrom: string | null };

/**
 * Chooses the invite page's state from the invite and this browser's session.
 * Team names are unique among teams, so an account whose current team bears
 * the invite's team name is in that team. `movingFrom` names the team the
 * account leaves by joining.
 */
export function existingAccountStep(
  invite: ExistingAccountInvite,
  session: InviteSession | undefined,
): ExistingAccountStep {
  if (invite.currentTeam?.name === invite.team) return { kind: "already-member" };
  const movingFrom = invite.currentTeam?.name ?? null;
  if (session === undefined) return { kind: "checking" };
  if (session.status === "unavailable") return { kind: "unavailable" };
  if (session.status === "signed-in" && sameEmail(session.email, invite.email)) {
    return { kind: "join", movingFrom };
  }
  return {
    kind: "sign-in-and-join",
    signedInAs: session.status === "signed-in" ? session.email : null,
    movingFrom,
  };
}

const sameEmail = (left: string, right: string) => left.toLowerCase() === right.toLowerCase();

export type AcceptFailure =
  | { readonly kind: "refused"; readonly code: string }
  | { readonly kind: "unavailable" };

export type AcceptResult =
  | { readonly ok: true; readonly team: { readonly name: string } }
  | { readonly ok: false; readonly failure: AcceptFailure };

export async function acceptInvite(
  token: string,
  fetchImpl: typeof fetch = fetch,
): Promise<AcceptResult> {
  const unavailable = { ok: false, failure: { kind: "unavailable" } } as const;
  let response: Response;
  try {
    response = await fetchImpl(`${platformApiBaseUrl}/api/invites/accept`, {
      method: "POST",
      credentials: "include",
      cache: "no-store",
      headers: { accept: "application/json", "content-type": "application/json" },
      body: JSON.stringify({ token }),
    });
  } catch {
    return unavailable;
  }
  const body: unknown = await response.json().catch(() => undefined);
  if (response.status >= 500) return unavailable;
  const record = typeof body === "object" && body !== null ? (body as Record<string, unknown>) : {};
  if (!response.ok) {
    const code =
      typeof record.code === "string" && record.code.length > 0
        ? record.code
        : response.status === 401
          ? "UNAUTHENTICATED"
          : "UNKNOWN";
    return { ok: false, failure: { kind: "refused", code } };
  }
  const organization = record.organization as Record<string, unknown> | null | undefined;
  if (typeof organization !== "object" || organization === null) return unavailable;
  if (typeof organization.name !== "string") return unavailable;
  return { ok: true, team: { name: organization.name } };
}

const ACCEPT_REFUSALS: Record<string, string> = {
  ALREADY_MEMBER: "common/youAreAlreadyIn_8dtlpo",
  INVITE_USED: "common/thisInviteHasAlready_8m17gy",
  INVITE_EXPIRED: "common/thisInviteHasExpired_57a2yy",
  INVITE_INVALID: "common/thisInviteIsNot_9zpc4p",
  INVITE_EMAIL_MISMATCH: "common/theInviteWasSent_pzlqpx",
  UNAUTHENTICATED: "common/yourSessionHasEnded_xzga1v",
};

export function acceptFailureMessage(failure: AcceptFailure): string {
  if (failure.kind === "unavailable") return t("common/couldNotAcceptThe_mu2eg7");
  return t(ACCEPT_REFUSALS[failure.code] ?? "common/couldNotAcceptThe_1au329");
}

export type SignInAndJoinOutcome =
  | {
      readonly kind: "joined";
      readonly team: { readonly name: string };
    }
  /** The password was refused or sign-in did not answer; the invite is untouched. */
  | { readonly kind: "sign-in-failed"; readonly error: AuthClientError }
  /** Signed in as the invited account, which did not join. */
  | { readonly kind: "accept-failed"; readonly failure: AcceptFailure };

/** The effects of signing in and joining, injected so their order is assertable. */
export interface SignInAndJoinFlow {
  /**
   * The sign-out wrapper of `~/shared/auth`, which drops the `dvk_` held in
   * `localStorage` and in the local server along with the session.
   */
  readonly signOut: () => Promise<unknown>;
  /** Drops the memoized auth gate state and the in-memory server token. */
  readonly invalidateGateState: () => void;
  /** Better Auth's `signIn.email`. */
  readonly signIn: (input: {
    readonly email: string;
    readonly password: string;
  }) => Promise<{ readonly error?: AuthClientError | null }>;
  readonly accept: (token: string) => Promise<AcceptResult>;
}

/**
 * Signs in as the invited account and joins its team: two requests, sign-in
 * first, because accepting needs that account's session.
 *
 * Another account signed in to this browser is signed out first, as the
 * sign-out button does it. A sign-in alone replaces only the session cookie
 * and leaves that account's `dvk_` in `localStorage` and in the local server,
 * where the next bootstrap or a running thread keeps spending it. A failed
 * sign-out does not stop the sign-in: the credential is dropped either way,
 * and the new session cookie replaces the old one.
 */
export async function signInAndJoin(
  flow: SignInAndJoinFlow,
  input: {
    readonly token: string;
    readonly email: string;
    readonly password: string;
    readonly signedInAs: string | null;
  },
): Promise<SignInAndJoinOutcome> {
  if (input.signedInAs !== null) {
    try {
      await flow.signOut();
    } catch {
      // The sign-in goes ahead; see above.
    }
    flow.invalidateGateState();
  }
  let error: AuthClientError | null | undefined;
  try {
    ({ error } = await flow.signIn({ email: input.email, password: input.password }));
  } catch {
    // better-fetch rethrows a network failure instead of returning it.
    error = { code: null };
  }
  if (error) return { kind: "sign-in-failed", error };
  // The gate answers from a memo; the session it memoized is not this one.
  flow.invalidateGateState();
  const accepted = await flow.accept(input.token);
  return accepted.ok
    ? { kind: "joined", team: accepted.team }
    : { kind: "accept-failed", failure: accepted.failure };
}
